// src/pages/Contact.jsx
import React, { useState } from 'react';
import {
    Container, Paper, Typography, Grid, TextField, Button, Alert, Box, Divider,
} from '@mui/material';
import contactImg from '../assets/contact/contact.JPG';
import { apiFetch } from '../utils/api';

function stripCtl(s) {
    return String(s || '').replace(/[\u0000-\u001F\u007F]/g, '');
}
function clamp(s, n) {
    const t = stripCtl(s);
    return t.length > n ? t.slice(0, n) : t;
}

export default function Contact() {
    const [first, setFirst] = useState('');
    const [last, setLast] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [msg, setMsg] = useState('');

    // Honeypot — use "company" to match backend (we’ll also keep "website" just in case bots fill it)
    const [company, setCompany] = useState('');
    const [website, setWebsite] = useState('');

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [okMsg, setOkMsg] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setOkMsg('');

        if (!first.trim() || !last.trim() || !email.trim() || !msg.trim()) {
            setError('Please complete all required fields.');
            return;
        }

        // If bots fill either honeypot, silently “succeed”
        if (company || website) {
            setOkMsg('Message sent!');
            return;
        }

        setBusy(true);
        try {
            const payload = {
                first_name: clamp(first, 100).trim(),
                last_name: clamp(last, 100).trim(),
                email: clamp(email, 120).trim(),
                phone: clamp(phone.replace(/[^\d()+\-\s]/g, ''), 20),
                message: clamp(msg, 2000).trim(),
                // honeypots: backend strips unknowns, but we align names anyway
                company: '',
            };

            // Use shared API wrapper so it respects REACT_APP_API_BASE when desired
            const res = await apiFetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(
                    data?.error
                        ? (Array.isArray(data.error) ? data.error.join(', ') : data.error)
                        : 'Unable to send your message right now.'
                );
            }

            setOkMsg('Message sent! Thank you for reaching out.');
            setFirst(''); setLast(''); setEmail(''); setPhone(''); setMsg('');
            setCompany(''); setWebsite('');
        } catch (err) {
            setError(err.message || 'Something went wrong.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ my: { xs: 4, md: 6 }, px: { xs: 2, md: 0 } }}>
            {/* Heading */}
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ fontWeight: 700, fontSize: { xs: '1.6rem', sm: '1.8rem' }, lineHeight: { xs: 1.25, sm: 1.25 } }}
                >
                    Contact Us
                </Typography>
            </Box>

            {/* Image + Form */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, sm: 4, md: 6 }, alignItems: 'stretch' }}>
                {/* Left image */}
                <Box sx={{ width: { xs: '100%', md: '38%' } }}>
                    <Box
                        component="img"
                        src={contactImg}
                        alt="Get in touch"
                        sx={{ width: '100%', height: 'auto', borderRadius: 2, boxShadow: { xs: 1, md: 0 }, objectFit: 'cover' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Dr. Benjamin Ingram - Piedmont Historical Society Project Director
                    </Typography>
                </Box>

                {/* Right form */}
                <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: 2, flex: 1 }}>
                    {/* ARIA live region */}
                    <Box role="status" aria-live="polite" sx={{ mb: 2 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {okMsg && <Alert severity="success">{okMsg}</Alert>}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Send us a message! We'd love to hear from you.
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    {/* FORM */}
                    <Box component="form" onSubmit={onSubmit} noValidate autoComplete="on" sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                        <Grid container spacing={{ xs: 2, md: 2.5 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="First Name"
                                    value={first}
                                    onChange={(e) => setFirst(clamp(e.target.value, 100))}
                                    required
                                    inputProps={{ maxLength: 100 }}
                                    disabled={busy}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Last Name"
                                    value={last}
                                    onChange={(e) => setLast(clamp(e.target.value, 100))}
                                    required
                                    inputProps={{ maxLength: 100 }}
                                    disabled={busy}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(clamp(e.target.value, 120))}
                                    required
                                    inputProps={{ maxLength: 120, inputMode: 'email' }}
                                    disabled={busy}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Phone (optional)"
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(clamp(e.target.value.replace(/[^\d()+\-\s]/g, ''), 20))}
                                    inputProps={{ maxLength: 20, inputMode: 'tel', pattern: '[0-9()+\\-\\s]+' }}
                                    disabled={busy}
                                />
                            </Grid>
                        </Grid>

                        {/* Message full-width */}
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                label="Message"
                                value={msg}
                                onChange={(e) => setMsg(clamp(e.target.value, 2000))}
                                required
                                multiline
                                minRows={6}
                                maxRows={12}
                                inputProps={{ maxLength: 2000 }}
                                disabled={busy}
                            />
                        </Box>

                        {/* Honeypot fields (hidden) */}
                        <Box sx={{ display: 'none' }} aria-hidden="true">
                            <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} tabIndex={-1} autoComplete="off" />
                            <TextField label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={busy}
                                aria-label="Send message"
                                sx={{
                                    px: 3, py: 1.25, textTransform: 'none', fontWeight: 700,
                                    '&.Mui-disabled': { color: 'common.white', bgcolor: 'primary.main', opacity: 0.75 },
                                }}
                            >
                                {busy ? 'Sending…' : 'Send Message'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}
